import React, { useState, useEffect, useRef, Suspense } from 'react';
import {
  Sheet,
  SheetTrigger,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from './ui/sheet';
import { Menu, CalendarDays } from 'lucide-react';
import { getProjects } from '../lib/api';
import lazyWithReload from '../lib/lazyWithReload';

// NAV-FE-1b — the booking modal (and the map picker in front of it, with its
// pan/zoom canvas) are the heaviest things the shell carried onto every page.
// They now load the first time this drawer OPENS (warmed below), and render
// through Suspense only when the booking flow actually mounts them.
const loadMapPicker = () => import('./AssetMapPicker');
const loadBookingModal = () => import('./MultiItemBookingModal');
const AssetMapPicker = lazyWithReload(loadMapPicker, 'AssetMapPicker');
const MultiItemBookingModal = lazyWithReload(loadBookingModal, 'MultiItemBookingModal');

// Delay before opening the booking modal AFTER the asset picker closes — same
// reason as InspectionsPage: two chained Radix Dialogs (picker -> modal) leak
// the "Done" pointer into the freshly-mounted modal and trip its
// dismiss-on-outside; deferring the mount one short beat avoids it.
const MODAL_OPEN_DELAY_MS = 200;

// Quick actions drawer — a SEPARATE menu from the profile/navigation menu on
// the right. Opens from a hamburger on the LEFT of the global header. First
// (and for now only) action: Book inspection -> the existing
// MultiItemBookingModal. Built around an actions[] array so future actions
// (NCR, RFI, ...) just get appended. No navigation lives here (that stays in
// the profile menu).
export default function QuickActionsMenu({ projectId }) {
  const [open, setOpen] = useState(false);          // the drawer
  // Book inspection flow: asset picker first, then the booking modal with the
  // chosen assets preselected (same pattern as InspectionsPage; duplicated here
  // rather than extracted to a shared file to keep this change to the two caller
  // files — a new component would widen the diff).
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerLots, setPickerLots] = useState([]);
  const openModalTimer = useRef(null);
  useEffect(() => () => clearTimeout(openModalTimer.current), []);
  const [projectTimezone, setProjectTimezone] = useState('');

  // NAV-FE-1b — warm the two chunks as soon as the drawer opens, so "Book
  // inspection" finds them loaded. A failure here is silent: the real load,
  // through lazyWithReload, retries when the picker mounts.
  useEffect(() => {
    if (!open || !projectId) return;
    loadMapPicker().catch(() => {});
    loadBookingModal().catch(() => {});
  }, [open, projectId]);

  // Resolve the project's timezone lazily, the first time the drawer opens, so
  // pages where the user never opens Quick actions pay nothing. The modal falls
  // back to its own default tz if this never resolves.
  useEffect(() => {
    if (!open || !projectId || projectTimezone) return;
    let cancelled = false;
    getProjects()
      .then(res => {
        if (cancelled) return;
        const tz = (res.data || []).find(p => p.id === projectId)?.timezone || '';
        setProjectTimezone(tz);
      })
      .catch(() => { /* keep empty -> modal uses its fallback tz */ });
    return () => { cancelled = true; };
  }, [open, projectId, projectTimezone]);

  const actions = [
    {
      key: 'book-inspection',
      label: 'Book inspection',
      icon: CalendarDays,
      disabled: !projectId,
      disabledHint: 'Open a project first',
      onSelect: () => { setOpen(false); setPickerOpen(true); },
    },
  ];

  return (
    <>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <button
            type="button"
            aria-label="Quick actions"
            data-testid="quick-actions-trigger"
            className="h-9 w-9 rounded-lg flex items-center justify-center text-slate-300 hover:text-white hover:bg-slate-800 transition-colors"
          >
            <Menu className="h-5 w-5" />
          </button>
        </SheetTrigger>
        <SheetContent side="left" className="bg-slate-900 border-slate-800 w-72 p-0">
          <SheetHeader className="px-4 py-4 border-b border-slate-800">
            <SheetTitle className="text-white text-base">Quick actions</SheetTitle>
          </SheetHeader>
          <div className="p-3 space-y-1" data-testid="quick-actions-list">
            {actions.map(a => {
              const Icon = a.icon;
              return (
                <button
                  key={a.key}
                  type="button"
                  onClick={a.disabled ? undefined : a.onSelect}
                  disabled={a.disabled}
                  title={a.disabled ? a.disabledHint : undefined}
                  data-testid={`quick-action-${a.key}`}
                  className="w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm text-slate-200 hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <Icon className="h-4 w-4 text-orange-400 shrink-0" />
                  <span className="flex-1">{a.label}</span>
                </button>
              );
            })}
            {!projectId && (
              <p className="px-3 pt-1 text-[11px] text-slate-500">
                Open a project first to book an inspection.
              </p>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <Suspense fallback={null}>
      {pickerOpen && projectId && (
        <AssetMapPicker
          projectId={projectId}
          onConfirm={(lots) => {
            // Close the picker FIRST, then mount the modal a beat later so the
            // "Done" pointer doesn't bleed into the modal's Dialog (chained
            // Radix Dialogs). See MODAL_OPEN_DELAY_MS.
            setPickerOpen(false);
            clearTimeout(openModalTimer.current);
            openModalTimer.current = setTimeout(() => setPickerLots(lots), MODAL_OPEN_DELAY_MS);
          }}
          onClose={() => setPickerOpen(false)}
        />
      )}
      {pickerLots.length > 0 && projectId && (
        <MultiItemBookingModal
          projectId={projectId}
          projectTimezone={projectTimezone}
          selectedLots={pickerLots}
          onChangeAssets={() => { setPickerLots([]); setPickerOpen(true); }}
          onClose={() => setPickerLots([])}
          onBooked={() => setPickerLots([])}
        />
      )}
      </Suspense>
    </>
  );
}
